import { SortingState } from "@tanstack/react-table";
import TableContainer from "Common/TableContainer";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { createSelector } from "@reduxjs/toolkit";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import SearchBox from "Components/SearchBox/SearchBox";
import { formatDate } from "helpers/functions/formatDate";
import { useHandleBack } from "helpers/functions/useHandleBack";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import { fetchAllItemSyncLogs } from "slices/item/thunk";

const WebSyncLog = () => {
  document.title = "Web Sync Log";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectItem = createSelector(
    (state: any) => state.ItemReducer || [],
    (item) => ({
      syncLogs: item.syncLogs,
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    })
  );

  const { syncLogs, rowCount, loading, error } = useSelector(selectItem);

  // State for sync logs
  //   const [syncLogs, setSyncLogs] = useState([]);
  //   const [rowCount, setRowCount] = useState(0);
  //   const [loading, setLoading] = useState(false);
  //   const [error, setError] = useState(null);

  // For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page_sync_log") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<any>();
  const [colValue, setColValue] = useState<any>();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const handleBack = useHandleBack("/item");

  useEffect(() => {
    dispatch(
      fetchAllItemSyncLogs({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortDirection,
        search,
        filters: colValue,
      })
    ).unwrap();
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    colValue,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    setData(syncLogs);
  }, [syncLogs]);

  // Sync pageIndex with URL parameters
  useEffect(() => {
    const currentPageParam = parseInt(searchParams.get("page_sync_log") || "0");
    if (currentPageParam !== pageIndex) {
      setPageIndex(currentPageParam);
    }
  }, [searchParams, pageIndex]);

  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPageIndex(0);
  }, [sorting, pageSize, columnFilters, search]);

  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page_sync_log: newPage.toString() });
    sessionStorage.setItem("syncLogPage", newPage.toString());
  };

  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
      }
    };
    fetchPermissions();
  }, [dispatch]);

  const columns = useMemo(
    () => [
      {
        header: "Item Name",
        accessorKey: "item__item_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/api/item/item-sync-dropdaown/?field=item__item_name",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              minWidth: "200px",
              maxWidth: "250px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.original.item_name || "-"}
          </div>
        ),
      },
      {
        header: "Item SKU",
        accessorKey: "item__item_sku",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/api/item/item-sync-dropdaown/?field=item__item_sku",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.original.item_sku || "-"}
          </div>
        ),
      },
      {
        header: "Category",
        accessorKey: "category__category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/item-sync-dropdaown/?field=category__category_name",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              minWidth: "120px",
              maxWidth: "200px",
            }}
          >
            {row.original.category_name || "-"}
          </div>
        ),
      },
      {
        header: "Selling Price",
        accessorKey: "selling_price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) =>
          `₹ ${Number(row.original.selling_price || 0).toFixed(2)}`,
      },
      {
        header: "Sale Price",
        accessorKey: "sale_price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) =>
          `₹ ${Number(row.original.sale_price || 0).toFixed(2)}`,
      },
      {
        header: "Purchase Price",
        accessorKey: "purchase_price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) =>
          `₹ ${Number(row.original.purchase_price || 0).toFixed(2)}`,
      },
      {
        header: "Regular Price (MRP)",
        accessorKey: "regular_price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) =>
          `₹ ${Number(row.original.regular_price || 0).toFixed(2)}`,
      },
      //   {
      //     header: "Updated Rate",
      //     accessorKey: "synced_response.price",
      //     enableColumnFilter: false,
      //     enableSorting: true,
      //     cell: ({ row }: { row: any }) => {
      //       const syncedPrice = row.original.synced_response?.price;
      //       return syncedPrice ? `₹ ${Number(syncedPrice).toFixed(2)}` : "-";
      //     },
      //   },
      //   {
      //     header: "Previous Rate",
      //     accessorKey: "synced_response.regular_price",
      //     enableColumnFilter: false,
      //     enableSorting: true,
      //     cell: ({ row }: { row: any }) => {
      //       const regularPrice = row.original.synced_response?.regular_price;
      //       return regularPrice ? `₹ ${Number(regularPrice).toFixed(2)}` : "-";
      //     },
      //   },
      {
        header: "Sync Status",
        accessorKey: "sync_status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const syncedResponse = row.original.synced_response;
          if (syncedResponse && syncedResponse.id) {
            return <span className="badge bg-success text-white">Synced</span>;
          } else {
            return (
              <span className="badge bg-danger text-white">Sync Failed</span>
            );
          }
        },
      },
      {
        header: "Sync Message",
        accessorKey: "sync_message",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const syncedResponse = row.original.synced_response;
          if (syncedResponse && syncedResponse.id) {
            return <span className="">Successfully Synced</span>;
          } else {
            return (
              <span className="text-wrap">
                {syncedResponse || "Sync Failed"}
              </span>
            );
          }
        },
      },
      {
        header: "Sync By",
        accessorKey: "sync_by__username",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/item-sync-dropdaown/?field=sync_by__username",
        cell: ({ row }: { row: any }) => {
          return row.original.sync_by_name || "-";
        },
      },
      {
        header: "Sync Date",
        accessorKey: "sync_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const date = formatDate(row.original.sync_at);
          return date;
        },
      },
    ],
    []
  );

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="syncLogList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2 mb-3">
                    {/* Left: Back icon and heading */}
                    <Col
                      xs={12}
                      md="auto"
                      className="d-flex align-items-center gap-2 mb-2 mb-md-0"
                    >
                      <span
                        style={{ fontSize: "24px", cursor: "pointer" }}
                        onClick={() => navigate("/item")}
                      >
                        ←
                      </span>
                      <h5 className="mb-0 fw-bold">Web Sync Log</h5>
                    </Col>
                    {/* Right: Search box and breadcrumb */}
                    <Col
                      xs={12}
                      md
                      className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-end gap-2"
                    >
                      <div className="order-2 order-md-1">
                        <Col xl={12} md={12}>
                          <div style={{ width: "300px" }}>
                            <SearchBox
                              value={searchInput}
                              onChange={setSearchInput}
                              placeholder="Search"
                            />
                          </div>
                        </Col>
                      </div>
                      <div className="text-muted order-1 order-md-2">
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.9rem", cursor: "pointer" }}
                          onClick={handleBack}
                        >
                          Item
                        </span>{" "}
                        &gt;{" "}
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.9rem", cursor: "pointer" }}
                        >
                          Web Sync Log
                        </span>{" "}
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={data}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting={true}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      SearchPlaceholder="Search Sync Logs..."
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default WebSyncLog;
